import { Grid, LinearProgress, Stack, Typography } from "@mui/material";
import noop from "lodash/noop";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";
import { AssociatedFilesView } from "../components/associatedFiles/AssociatedFilesView";
import DatePicker from "../components/datePicker/DatePicker";
import { InfoEdit } from "../components/editDialog/InfoEdit";
import NumberField from "../components/numberField/NumberField";
import ProductsTable from "../components/productsTable/ProductsTable";
import dayjs from "../dayjs";
import { Price } from "../misc/price";
import BackToOrderListButton from "./BackToOrderListButton";
import CreateCalculationButton from "./CreateCalculationButton";
import DownloadOrderPdfButton from "./DownloadOrderPdfButton";
import GoToParticipantListButton from "./GoToParticipantListButton";
import SendCheckInButton from "./emails/SendCheckInButton";
import OrderInvoices from "./invoices/OrderInvoices";
import Confirmations from "./orderConfirmation/Confirmations";
import ListTemplates from "./orderEdit/ListTemplates";
import MoneyValue from "./orderEdit/MoneyValue";
import { sumPayments } from "./orderEdit/OrderPayments";
import SupervisorInfo from "./orderEdit/SupervisorInfo";
import { getTotalPrice } from "./orderEdit/getTotalPrice";
import type { Product } from "../components/productsTable/ProductsTable";
import { useSavedOrderToProducts } from "./orderEdit/orderToProducts";
import { OrderStatusSelect } from "./orderStatus/OrderStatusSelect";
import { useOrder } from "./useOrder";

const CustomerOrderPage = () => {
  const navigate = useNavigate();
  const { order, isLoading: isOrderLoading } = useOrder();
  const { products, isLoading: areProductsLoading } = useSavedOrderToProducts(order);
  const { context } = useAuthContext();

  const isLoading = isOrderLoading || areProductsLoading;
  const canCreateCalculationFromProducts =
    context?.role === "CUSTOMER" && context?.customPrivileges.canCreateCalculationFromProducts;

  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!order) {
    return (
      <Grid
        container
        sx={{
          marginTop: 2,
          marginBottom: 2,
        }}
      >
        <Typography variant="h5">Takie zamówienie nie istnieje</Typography>
      </Grid>
    );
  }
  const totalPrice = getTotalPrice(
    order.products.map(({ price, cancelled }) => ({ price: new Price(price), cancelled })),
  );
  const paymentsSum = sumPayments(order.payments);
  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 2,
        paddingBottom: 2,
      }}
    >
      <Grid
        size={12}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "baseline",
          }}
        >
          <Typography variant="h6">Zamówienie {order.userFriendlyId}</Typography>
          {order.created && <Typography variant="body2">{dayjs(order.created).format("DD.MM.YYYY")}</Typography>}
        </Stack>
      </Grid>
      <Grid size={12}>
        <Typography variant="h5">{order.name}</Typography>
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }} container spacing={2}>
        <Grid size={6}>
          <OrderStatusSelect value={order.status} role="CUSTOMER" onChange={noop} />
        </Grid>
        <Grid size={6}>
          <NumberField
            label="Liczba osób płatnych"
            fullWidth
            value={order.participantsNumber.numberOfPaid}
            readonly
            onChange={noop}
          />
        </Grid>
        <Grid size={6}>
          <NumberField
            label="Liczba osób obsługi"
            fullWidth
            value={order.participantsNumber.numberOfStaff}
            readonly
            onChange={noop}
          />
        </Grid>
        <Grid size={12}>
          <SupervisorInfo
            supervisorId={order.supervisorId}
            customerId={order.customerId}
            order={{ id: order.userFriendlyId, name: order.name, startDate: order.start ?? "" }}
          />
        </Grid>
        <Grid size={12}>
          <InfoEdit value={order.info} readonly onChange={noop} />
        </Grid>
      </Grid>
      <Grid size={{ xs: 12, lg: 6 }} container spacing={2}>
        <Grid size={6}>
          <NumberField
            label="Kwota zaliczki"
            fullWidth
            readonly
            value={order.advance?.amount}
            suffix={order.currency}
            onChange={noop}
          />
        </Grid>
        <Grid size={6}>
          <DatePicker label="Termin zaliczki" fullWidth readonly value={order.advance?.deadline} onChange={noop} />
        </Grid>
        <Grid size={6}>
          <MoneyValue label="Suma wpłat" value={paymentsSum} currency={order.currency} />
        </Grid>
        <Grid size={6}>
          <MoneyValue
            label="Do zapłaty"
            value={totalPrice.isPresent() ? (totalPrice.subtract(paymentsSum).value as number) : null}
            currency={order.currency}
          />
        </Grid>
        <Grid size={12}>
          <Stack direction="column" spacing={1}>
            <AssociatedFilesView values={order.files} groupByVisibility />
            <OrderInvoices order={order} readonly />
            <Confirmations orderId={order.id} readonly />
            <ListTemplates orderId={order.id} />
          </Stack>
        </Grid>
      </Grid>
      <Grid size={12}>
        <ProductsTable
          values={products as Product[]}
          currency={order.currency}
          totalPrice={totalPrice}
          hideProductPrices={order.type === "PACKAGE"}
          orderId={order.id}
        />
      </Grid>
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <BackToOrderListButton />
          {canCreateCalculationFromProducts && (
            <CreateCalculationButton onClick={() => navigate(`/calculations/new?fromOrder=${order.id}`)} />
          )}
          <GoToParticipantListButton order={order} onClick={() => navigate(`/orders/${order.id}/participant-lists`)} />
          <SendCheckInButton orderId={order.id} />
          <DownloadOrderPdfButton orderId={order.id} orderType={order.type} filename={`${order.name}.pdf`} />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default CustomerOrderPage;
